import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DownloadIcon from "@mui/icons-material/Download";
import SarIcon from "../../icons/SarIcon";
import {
  formatLongDate,
  formatPercent,
  formatSar,
  formatTerm,
  statusLabel,
} from "./loanFormatters";

const SECTION_TITLE_SX = { fontSize: 16, color: "text.primary", mb: 2 };

/** Grey (or tinted) detail tile: icon + label above the value. */
const DetailTile = ({
  icon,
  label,
  value,
  span,
  bg = "background.subtle",
  labelColor = "text.muted",
  valueColor = "text.primary",
  valueSize = 16,
}) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 1.5,
      bgcolor: bg,
      gridColumn: span ? { xs: "1 / -1", sm: `span ${span}` } : undefined,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography sx={{ fontSize: 12.8, color: labelColor }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: valueSize, color: valueColor, mt: 1 }}>
      {value}
    </Typography>
  </Box>
);

/** Two-column tile grid; collapses to one column on narrow screens. */
const TileGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: 2,
    }}
  >
    {children}
  </Box>
);

const TimelineRow = ({ icon, label, value, bg, labelColor, valueColor }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{ p: 1.5, borderRadius: 1.5, bgcolor: bg }}
  >
    {icon}
    <Box>
      <Typography sx={{ fontSize: 12, color: labelColor }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, color: valueColor }}>{value}</Typography>
    </Box>
  </Stack>
);

/** Read-only view of a full loan application. */
const AdminLoanDetailsModal = ({ open, loan, onClose }) => {
  const details = loan?.activeDetails || {};
  const collateral = details.collateral || {};
  const currency = details.currency || loan?.currency;
  const labelIconSx = { fontSize: 16, color: "text.muted" };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1.5,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
        },
      }}
    >
      <Box sx={{ p: 3, position: "relative" }}>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.muted", opacity: 0.7 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "text.primary", pr: 5 }}>
          {`Loan Details - ${loan?.loanId || "—"}`}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 1 }}>
          Complete loan application information
        </Typography>

        {/* Applicant */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mt: 3, pb: 3 }}
        >
          <Avatar
            src={loan?.user?.profileImage || undefined}
            alt={loan?.user?.name || ""}
            sx={{
              width: 80,
              height: 80,
              flexShrink: 0,
              border: (theme) => `2px solid ${theme.palette.accent.cyan.border}`,
              bgcolor: (theme) => theme.palette.statIcon.users.bg,
              color: (theme) => theme.palette.statIcon.users.color,
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            {(loan?.user?.name || "?").charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, color: "text.primary" }}>
              {loan?.user?.name || "—"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: "text.muted", mt: 0.5 }}>
              {loan?.user?.email || "—"}
            </Typography>
          </Box>
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
            label={statusLabel(loan?.status)}
            sx={{
              flexShrink: 0,
              height: 34,
              borderRadius: 1.25,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "background.search",
              color: "text.neutral",
              border: (theme) => `1px solid ${theme.palette.border.medium}`,
              "& .MuiChip-icon": { color: "text.neutral" },
            }}
          />
        </Stack>

        <Divider />

        {/* Borrower information */}
        <Typography sx={{ ...SECTION_TITLE_SX, mt: 3 }}>Borrower Information</Typography>
        <TileGrid>
          <DetailTile
            icon={<PersonOutlineIcon sx={labelIconSx} />}
            label="Full Name"
            value={loan?.user?.name || "—"}
          />
          <DetailTile
            icon={<MailOutlineIcon sx={labelIconSx} />}
            label="Email"
            value={loan?.user?.email || "—"}
          />
          <DetailTile
            icon={<PhoneOutlinedIcon sx={labelIconSx} />}
            label="Phone"
            value={loan?.user?.phone || "—"}
          />
          <DetailTile
            icon={<LocationOnOutlinedIcon sx={labelIconSx} />}
            label="Location"
            value={loan?.user?.location || "—"}
          />
          <DetailTile
            icon={<BadgeOutlinedIcon sx={labelIconSx} />}
            label="National ID"
            value={loan?.user?.nationalId || "—"}
            span={2}
          />
        </TileGrid>

        {/* Financial information */}
        <Typography sx={{ ...SECTION_TITLE_SX, mt: 3 }}>
          Financial Information
        </Typography>
        <TileGrid>
          <DetailTile
            icon={<SarIcon sx={{ fontSize: 16, color: (theme) => theme.palette.credit.score }} />}
            label="Annual Income"
            value={loan?.annualIncome ? formatSar(loan.annualIncome, currency) : "—"}
            bg={(theme) => theme.palette.credit.bg}
            labelColor={(theme) => theme.palette.credit.score}
            valueColor={(theme) => theme.palette.credit.score}
            valueSize={20}
          />
          <DetailTile
            icon={
              <WorkOutlineIcon
                sx={{ fontSize: 16, color: (theme) => theme.palette.statIcon.assets.color }}
              />
            }
            label="Employment"
            value={loan?.employmentStatus || "—"}
            bg={(theme) => theme.palette.statIcon.assets.bg}
            labelColor={(theme) => theme.palette.statIcon.assets.color}
            valueColor={(theme) => theme.palette.statIcon.assets.color}
            valueSize={20}
          />
        </TileGrid>

        {/* Loan details */}
        <Typography sx={{ ...SECTION_TITLE_SX, mt: 3 }}>Loan Details</Typography>
        <TileGrid>
          <DetailTile
            label="Loan Amount"
            value={formatSar(loan?.amount, currency)}
            valueSize={20}
          />
          <DetailTile
            label="Interest Rate"
            value={
              Number.isFinite(Number(loan?.interestRate))
                ? `${loan.interestRate}% p.a.`
                : "—"
            }
            valueSize={20}
          />
          <DetailTile
            label="Loan Term"
            value={formatTerm(loan?.termMonths)}
            valueSize={20}
          />
          <DetailTile
            label="Monthly Payment"
            value={formatSar(loan?.monthlyPayment, currency)}
            valueSize={20}
          />
          <DetailTile
            label="LTV Ratio"
            value={formatPercent(loan?.ltvRatio)}
            valueSize={20}
            span={2}
          />
        </TileGrid>

        {/* Property information */}
        <Typography sx={{ ...SECTION_TITLE_SX, mt: 3 }}>Property Information</Typography>
        <TileGrid>
          <DetailTile
            icon={<ApartmentOutlinedIcon sx={labelIconSx} />}
            label="Property Name"
            value={collateral.assetName || loan?.assetName || "—"}
          />
          <DetailTile
            icon={<HomeWorkOutlinedIcon sx={labelIconSx} />}
            label="Property Type"
            value={loan?.propertyType || "—"}
          />
          <DetailTile
            icon={<LocationOnOutlinedIcon sx={labelIconSx} />}
            label="Location"
            value={loan?.assetLocation || "—"}
          />
          <DetailTile
            icon={<DescriptionOutlinedIcon sx={labelIconSx} />}
            label="Ownership"
            value={
              Number.isFinite(Number(collateral.ownershipPercent))
                ? `${collateral.ownershipPercent}%`
                : formatPercent(loan?.collateralOwnershipPercent)
            }
          />
          <DetailTile
            icon={<SarIcon sx={labelIconSx} />}
            label="Collateral Value"
            value={collateral.value != null ? formatSar(collateral.value, currency) : "—"}
          />
          <DetailTile
            icon={<DescriptionOutlinedIcon sx={labelIconSx} />}
            label="Loan Purpose"
            value={loan?.purpose || "—"}
          />
        </TileGrid>

        {/* Timeline */}
        <Typography sx={{ ...SECTION_TITLE_SX, mt: 3 }}>Timeline</Typography>
        <Stack spacing={1.5}>
          <TimelineRow
            icon={<CalendarTodayIcon sx={{ fontSize: 20, color: "text.muted" }} />}
            label="Request Date"
            value={formatLongDate(loan?.appliedAt)}
            bg="background.subtle"
            labelColor="text.muted"
            valueColor="text.primary"
          />
          {loan?.reviewedAt ? (
            <TimelineRow
              icon={
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 20, color: (theme) => theme.palette.credit.score }}
                />
              }
              label="Approval Date"
              value={formatLongDate(loan.reviewedAt)}
              bg={(theme) => theme.palette.credit.bg}
              labelColor={(theme) => theme.palette.credit.score}
              valueColor={(theme) => theme.palette.credit.score}
            />
          ) : null}
        </Stack>

        {/* Footer */}
        <Divider sx={{ mt: 3 }} />
        <Button
          fullWidth
          variant="contained"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          sx={{
            mt: 3,
            height: 36,
            borderRadius: 1.25,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            color: "common.white",
            background: (theme) => theme.palette.gradients.cyanBlue,
            boxShadow: "none",
            "&:hover": {
              background: (theme) => theme.palette.gradients.cyanBlueHover,
              boxShadow: "none",
            },
          }}
        >
          Download Loan Confirmation
        </Button>
      </Box>
    </Dialog>
  );
};

export default AdminLoanDetailsModal;
